import { createContext, Dispatch, SetStateAction, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';

import { leaveFeedback } from 'app/providers/csi/api/feedback';
import FeedbackModal from 'app/providers/csi/components/modal/feedback';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';

import ConfirmationDialog from './components/confirmation-dialog/confirmation-dialog';

interface CsiState {
  open: boolean;
  business_key: string;
  endpoint: string;
  redirectUrl?: string;
  dialogLabel?: string;
}

const CsiContext = createContext<{
  csiState: CsiState;
  setCsiState?: Dispatch<SetStateAction<CsiState>>;
  closeModal: VoidFunction;
  openModal: (
    endpoint: string,
    business_key: string,
    redirectUrl?: string,
    dialogLabel?: string
  ) => void;
  openFeedbackDirect: (endpoint: string, business_key: string, redirectUrl?: string) => void;
  submit: (rating: number, comment: string) => void;
} | null>(null);

export const CsiProvider = ({ children }: any) => {
  const [csiState, setCsiState] = useState<CsiState>({
    open: false,
    business_key: '',
    endpoint: '',
    dialogLabel: '',
  });

  const navigate = useCustomNavigate();
  const [openFeedback, setOpenFeedback] = useState(false);
  const { lang } = useParams();
  const { t } = useTranslation();

  const closeModal = () => setCsiState(prevState => ({ ...prevState, open: false }));
  const openModal = (
    endpoint: string,
    business_key: string,
    redirectUrl?: string,
    dialogLabel?: string
  ) =>
    setCsiState(prevState => ({
      ...prevState,
      endpoint,
      business_key,
      redirectUrl,
      dialogLabel,
      open: true,
    }));

  const openFeedbackDirect = (endpoint: string, business_key: string, redirectUrl?: string) => {
    setCsiState(prev => ({ ...prev, endpoint, business_key, redirectUrl }));
    setOpenFeedback(true);
  };

  const { mutate: leaveFeedbackMutate } = useMutation({
    mutationKey: ['leaveFeedback', lang],
    mutationFn: leaveFeedback,
  });

  const submit = (rating: number, comment: string) => {
    const requestBody = {
      rating,
      comment,
      business_key: csiState.business_key,
      endpoint: csiState.endpoint,
    };

    const redirectAfterFeedback = csiState.redirectUrl;

    leaveFeedbackMutate(requestBody, {
      onSuccess: () => {
        if (redirectAfterFeedback) navigate(redirectAfterFeedback);
        closeModal();
      },
      onError: () => {
        if (redirectAfterFeedback) navigate(redirectAfterFeedback);
        closeModal();
      },
    });
  };

  const handleSubmitConfirmation = () => {
    setCsiState(prev => ({ ...prev, redirectUrl: 'home-page' }));
    setOpenFeedback(true);
  };

  const handleCloseConfirmation = () => {
    setOpenFeedback(true);
  };

  const handleClose = () => {
    if (csiState.redirectUrl) navigate(csiState.redirectUrl);
    closeModal();
    setOpenFeedback(false);
  };

  const getDialogLabel = () => {
    return csiState.dialogLabel ? csiState.dialogLabel : t('commonTerms.paymentSigned');
  };

  return (
    <CsiContext.Provider
      value={{ csiState, setCsiState, closeModal, openModal, openFeedbackDirect, submit }}
    >
      {children}
      <ConfirmationDialog
        label={getDialogLabel()}
        open={csiState.open}
        handleClose={handleCloseConfirmation}
        handleSubmit={handleSubmitConfirmation}
      />
      <FeedbackModal open={openFeedback} close={handleClose} submit={submit} />
    </CsiContext.Provider>
  );
};

export const useCsiContext = () => {
  const context = useContext(CsiContext);
  if (!context) {
    return {
      openModal: () => {},
      openFeedbackDirect: () => {},
    };
  }

  return context;
};
